import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { getAllSales } from "../../services/salesService";
import { getAllCategories } from "../../services/categoryService";
import { getAllProducts } from "../../services/productService";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
    BarChart2,
    Search,
    Calendar,
    Filter,
    FileDown,
    FileSpreadsheet,
    FileText,
    Printer,
    ChevronDown,
    ShoppingBag,
    TrendingUp,
    Tag,
    X,
    ArrowLeft,
    Receipt,
} from "lucide-react";

const fmt = (n) =>
    Number(n || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const today = () => new Date().toISOString().split("T")[0];
const monthStart = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
};

export default function SalesReport() {
    const navigate = useNavigate();
    const location = useLocation();

    const [sales, setSales] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showExport, setShowExport] = useState(false);

    // filters
    const [fromDate, setFromDate] = useState(location.state?.fromDate || monthStart());
    const [toDate, setToDate] = useState(location.state?.toDate || today());
    const [search, setSearch] = useState("");
    const [catFilter, setCatFilter] = useState("All");

    // ── fetch ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const [s, c, p] = await Promise.all([getAllSales(), getAllCategories(), getAllProducts()]);
                setSales(s);
                setCategories(c);
                setProducts(p);
            } catch {
                toast.error("Failed to load sales");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // ── filtered ───────────────────────────────────────────────────────────────
    const filtered = sales.filter((s) => {
        const dateOk = s.date >= fromDate && s.date <= toDate;
        const name = (s.billingPerson?.name || "").toLowerCase();
        const mobile = (s.billingPerson?.mobile || "").toLowerCase();
        const bill = (s.billNumber || "").toLowerCase();
        const q = search.toLowerCase();
        const searchOk = !q || name.includes(q) || mobile.includes(q) || bill.includes(q);
        return dateOk && searchOk;
    });

    // ── summary ────────────────────────────────────────────────────────────────
    const getSaleVP = (sale) => {
        return (sale.items || []).reduce((sum, item) => {
            const product = products.find(p => p.id === item.productId || p.name === item.productName);
            const vp = product ? Number(product.volPoint || 0) : 0;
            return sum + (vp * item.qty);
        }, 0);
    };

    const totalSales = filtered.length;
    const totalRevenue = filtered.reduce((s, r) => s + Number(r.total || 0), 0);
    const totalDiscount = filtered.reduce((s, r) => s + Number(r.discountAmt || 0), 0);
    const totalSubtotal = filtered.reduce((s, r) => s + Number(r.subtotal || 0), 0);
    const totalVP = filtered.reduce((s, r) => s + getSaleVP(r), 0);

    // ── export ─────────────────────────────────────────────────────────────────
    const buildExportRows = () =>
        filtered.map((s) => ({
            "Bill No": s.billNumber || "",
            Date: s.date || "",
            Time: s.time || "",
            "Customer Name": s.billingPerson?.name || "Walk-in",
            "Product Name & Qty": (s.items || []).map(i => `${i.productName} (${i.qty})`).join(", "),
            "Total Qty": (s.items || []).reduce((sum, i) => sum + i.qty, 0),
            "Total VP": getSaleVP(s).toFixed(2),
            Discount: Number(s.discountAmt || 0).toFixed(2),
            Total: Number(s.total || 0).toFixed(2),
            "Payment Mode": s.paymentType || "Cash",
        }));

    const handleExport = (format) => {
        const rows = buildExportRows();
        const filename = `Sales_Report_${fromDate}_to_${toDate}`;
        if (format === "Excel") {
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sales");
            XLSX.writeFile(wb, `${filename}.xlsx`);
        } else if (format === "CSV") {
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sales");
            XLSX.writeFile(wb, `${filename}.csv`);
        } else if (format === "PDF") {
            const doc = new jsPDF("landscape");
            doc.setFontSize(14);
            doc.text("Sales Report", 14, 14);
            doc.setFontSize(9);
            doc.text(`Period: ${fromDate} to ${toDate}`, 14, 21);
            autoTable(doc, {
                head: [Object.keys(rows[0] || {})],
                body: rows.map((r) => Object.values(r)),
                startY: 26,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [37, 99, 235] },
            });
            doc.save(`${filename}.pdf`);
        } else if (format === "Print") {
            window.print();
        }
        setShowExport(false);
        toast.success(`Exported as ${format}`);
    };

    // ── render ─────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-50 p-5">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate("/admin/sales")}
                        className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center">
                        <BarChart2 className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Sales Report</h1>
                        <p className="text-xs text-gray-500">Track all sales transactions</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate("/admin/sales")}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        New Sale
                    </button>

                    {/* Export */}
                    <div className="relative">
                        <button
                            onClick={() => setShowExport((p) => !p)}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                        >
                            <FileDown className="w-4 h-4" />
                            Export
                            <ChevronDown className="w-4 h-4" />
                        </button>
                        {showExport && (
                            <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20">
                                {[
                                    { label: "Excel", icon: <FileSpreadsheet className="w-4 h-4 text-green-600" /> },
                                    { label: "CSV", icon: <FileText className="w-4 h-4 text-blue-600" /> },
                                    { label: "PDF", icon: <FileDown className="w-4 h-4 text-red-600" /> },
                                    { label: "Print", icon: <Printer className="w-4 h-4 text-gray-600" /> },
                                ].map(({ label, icon }) => (
                                    <button
                                        key={label}
                                        onClick={() => handleExport(label)}
                                        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 text-sm first:rounded-t-xl last:rounded-b-xl"
                                    >
                                        {icon} {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-5">
                <div className="flex flex-wrap items-end gap-3">
                    {/* From Date */}
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* To Date */}
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* Search */}
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Name, mobile, bill no…"
                                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
                            />
                        </div>
                    </div>

                    {/* Clear */}
                    {(search || fromDate !== monthStart() || toDate !== today()) && (
                        <button
                            onClick={() => {
                                setSearch("");
                                setFromDate(monthStart());
                                setToDate(today());
                            }}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                        >
                            <X className="w-4 h-4" /> Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
                {[
                    {
                        label: "Total Bills",
                        value: totalSales,
                        icon: <Receipt className="w-5 h-5 text-blue-600" />,
                        bg: "bg-blue-50",
                        color: "text-blue-600",
                    },
                    {
                        label: "Gross Sales",
                        value: `₹${fmt(totalSubtotal)}`,
                        icon: <ShoppingBag className="w-5 h-5 text-indigo-600" />,
                        bg: "bg-indigo-50",
                        color: "text-indigo-600",
                    },
                    {
                        label: "Total Discount",
                        value: `₹${fmt(totalDiscount)}`,
                        icon: <Tag className="w-5 h-5 text-orange-600" />,
                        bg: "bg-orange-50",
                        color: "text-orange-600",
                    },
                    {
                        label: "Net Revenue",
                        value: `₹${fmt(totalRevenue)}`,
                        icon: <TrendingUp className="w-5 h-5 text-green-600" />,
                        bg: "bg-green-50",
                        color: "text-green-600",
                    },
                    {
                        label: "Total Sales VP",
                        value: `${totalVP.toFixed(2)} VP`,
                        icon: <TrendingUp className="w-5 h-5 text-purple-600" />,
                        bg: "bg-purple-50",
                        color: "text-purple-600",
                    },
                ].map(({ label, value, icon, bg, color }) => (
                    <div
                        key={label}
                        className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex items-center gap-3"
                    >
                        <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center`}>
                            {icon}
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">{label}</p>
                            <p className={`text-lg font-bold ${color}`}>{value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-900">
                        Transactions ({filtered.length})
                    </h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                {[
                                    "Bill No",
                                    "Date & Time",
                                    "Customer",
                                    "Products & Qty",
                                    "Total VP",
                                    "Discount",
                                    "Total",
                                    "Payment Type"
                                ].map((h) => (
                                    <th
                                        key={h}
                                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex justify-center">
                                            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center text-gray-400">
                                        <BarChart2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                        <p className="text-sm">No sales found for the selected filters</p>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((s) => (
                                    <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1 text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                                <Receipt className="w-3 h-3" />
                                                #{s.billNumber}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                                            <div className="flex flex-col gap-0.5">
                                                <span>
                                                    {s.date
                                                        ? new Date(s.date).toLocaleDateString("en-IN", {
                                                            day: "numeric",
                                                            month: "short",
                                                            year: "numeric",
                                                        })
                                                        : "—"}
                                                </span>
                                                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                                                    {s.time || "—"}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="font-medium text-gray-900 whitespace-nowrap">
                                                    {s.billingPerson?.name || "Walk-in Customer"}
                                                </span>
                                                {s.billingPerson?.mobile && (
                                                    <span className="text-[10px] text-gray-500">
                                                        {s.billingPerson.mobile}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-0.5">
                                                {(s.items || []).map((item, idx) => (
                                                    <span key={idx} className="text-xs text-gray-700">
                                                        {item.productName} <span className="font-bold text-blue-600 ml-1">× {item.qty}</span>
                                                    </span>
                                                ))}
                                                <span className="text-[10px] text-gray-400 mt-1 uppercase font-semibold">
                                                    Total Qty: {(s.items || []).reduce((sum, i) => sum + i.qty, 0)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">
                                            {getSaleVP(s).toFixed(2)} VP
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {s.discountPct > 0 ? (
                                                <div className="flex flex-col gap-1 items-start">
                                                    <span className="text-xs text-green-700 font-medium bg-green-100 px-2 py-0.5 rounded-full">
                                                        {s.discountPct}% OFF
                                                    </span>
                                                    <span className="text-xs text-gray-500 font-medium">
                                                        -₹{fmt(s.discountAmt)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">
                                            ₹{fmt(s.total)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold ${s.paymentType === "Gpay" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"
                                                }`}>
                                                {s.paymentType || "Cash"}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        {/* Footer totals */}
                        {!loading && filtered.length > 0 && (
                            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                                <tr>
                                    <td colSpan={4} className="px-4 py-3 font-bold text-gray-700 text-sm">
                                        TOTAL ({filtered.length} bills)
                                    </td>
                                    <td className="px-4 py-3 font-bold text-purple-700">
                                        {totalVP.toFixed(2)} VP
                                    </td>
                                    <td className="px-4 py-3 font-bold text-green-700">
                                        -₹{fmt(totalDiscount)}
                                    </td>
                                    <td className="px-4 py-3 font-bold text-blue-700">
                                        ₹{fmt(totalRevenue)}
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div >
    );
}
